/**
 * #/import — "traé tu histórico" (ítem 2.7 del plan).
 *
 * El momento de la reunión que produce esta pantalla: la federación llega con
 * una carpeta de `.dvw` de las últimas temporadas —los que hoy sólo abre
 * DataVolley— y en la mesa se eligen todos, se ve QUÉ va a entrar, y entra.
 * Sin consola, sin script, sin pedirle nada al proveedor anterior.
 *
 * Dos cosas que esta pantalla hace a propósito y conviene no "simplificar":
 *
 *  1. **Preview siempre antes de escribir.** Elegir archivos no importa nada:
 *     dispara un `dryRun` que devuelve exactamente el mismo análisis que se va
 *     a guardar. Recién el botón escribe. Importar 130 partidos a ciegas sobre
 *     la base de la federación no es algo que se deshaga con un botón — el log
 *     es append-only.
 *  2. **El reporte de lo que NO se pudo mapear se muestra entero.** El importer
 *     de `@scout/dvw` es best-effort y no descarta nada: lo que no entiende
 *     entra como comentario con su línea original. Mostrarlo es la diferencia
 *     entre "importamos todo" (que sería mentira si nadie lo mira) y "esto es
 *     lo que entró como evento y esto quedó como texto, con el código".
 *
 * Las tandas: el API acepta 25 archivos por request, así que una temporada se
 * parte sola (ver `lib/import-dvw.ts`). El operador no se entera salvo por el
 * contador de avance.
 */
import { useCallback, useState } from 'react';
import { Card, Empty, ErrorBox, Notice } from '../components/States';
import { errorMessage } from '../hooks';
import type { MessageKey } from '../i18n/index';
import { useT } from '../i18n/use-t';
import type { ApiClient, DvwFileResult, DvwImportFile, DvwImportResponse, Identity } from '../lib/api-client';
import {
  IMPORT_PERMISSION,
  TEAMS_PERMISSION,
  canImportDvw,
  canImportTeams,
} from '../lib/auth';
import { dateTime } from '../lib/format';
import {
  batches,
  importableFiles,
  looksLikeDvw,
  mergeResponses,
  rollupTeams,
  rollupUnmapped,
  toImportFile,
  totalEvents,
} from '../lib/import-dvw';
import { matchHash } from '../lib/router';

interface Progress {
  /** Tanda en curso (1..total). */
  batch: number;
  total: number;
  /** `true` mientras se escribe de verdad (no el preview). */
  writing: boolean;
}

export function ImportPage({
  client,
  identity,
}: {
  client: ApiClient;
  identity: Identity | null;
}): JSX.Element {
  const t = useT();
  const [files, setFiles] = useState<DvwImportFile[]>([]);
  const [preview, setPreview] = useState<DvwImportResponse | null>(null);
  const [result, setResult] = useState<DvwImportResponse | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const allowed = canImportDvw(identity);
  const canTeams = canImportTeams(identity);

  /** Corre la tanda entera (preview o import) y junta las respuestas. */
  const run = useCallback(
    async (payload: readonly DvwImportFile[], dryRun: boolean): Promise<DvwImportResponse | null> => {
      const groups = batches(payload);
      const responses: DvwImportResponse[] = [];
      setError(null);
      try {
        for (const [index, group] of groups.entries()) {
          setProgress({ batch: index + 1, total: groups.length, writing: !dryRun });
          responses.push(await client.importDvw(group, dryRun));
        }
        return mergeResponses(responses);
      } catch (err) {
        setError(errorMessage(err instanceof Error ? err : new Error(String(err))));
        return null;
      } finally {
        setProgress(null);
      }
    },
    [client],
  );

  const onFiles = async (list: FileList | null): Promise<void> => {
    setPreview(null);
    setResult(null);
    if (!list || list.length === 0) {
      setFiles([]);
      return;
    }
    const payload: DvwImportFile[] = [];
    for (const file of Array.from(list)) {
      // Se leen los BYTES, no el texto: el charset lo decide el server.
      payload.push(toImportFile(file.name, new Uint8Array(await file.arrayBuffer())));
    }
    setFiles(payload);
    setPreview(await run(payload, true));
  };

  const doImport = async (): Promise<void> => {
    const done = await run(files, false);
    if (done) {
      setResult(done);
      setPreview(null);
    }
  };

  const shown = result ?? preview;
  const sinDvw = files.filter((file) => !looksLikeDvw(file.name)).length;

  return (
    <div>
      <div className="page-head">
        <h1>{t('import.title')}</h1>
      </div>

      <Card>
        <p className="hint">{t('import.intro')}</p>
        {!allowed ? (
          <Notice
            message={t('import.noPermission')}
            hint={t('import.noPermissionHint', { permission: IMPORT_PERMISSION })}
          />
        ) : null}
        <label className="field">
          <span>{t('import.fileLabel')}</span>
          <input
            type="file"
            accept=".dvw,.DVW"
            multiple
            disabled={!allowed || progress !== null}
            onChange={(e) => void onFiles(e.target.files)}
          />
        </label>
        {files.length > 0 ? (
          <p className="hint">
            {t('import.chosen', { count: files.length })}
            {sinDvw > 0 ? ` · ${t('import.notDvw', { count: sinDvw })}` : ''}
          </p>
        ) : null}
        {allowed && !canTeams ? (
          <Notice
            message={t('import.noTeamsPermission')}
            hint={t('import.noTeamsPermissionHint', { permission: TEAMS_PERMISSION })}
          />
        ) : null}
        {progress !== null ? (
          <p className="loading" role="status">
            {progress.writing
              ? t('import.writingBatch', { batch: progress.batch, total: progress.total })
              : t('import.readingBatch', { batch: progress.batch, total: progress.total })}
          </p>
        ) : null}
        {error !== null ? <ErrorBox message={error} /> : null}
      </Card>

      {shown !== null ? (
        <>
          <SummaryCard
            response={shown}
            onImport={() => void doImport()}
            busy={progress !== null}
            allowed={allowed}
          />
          <UnmappedCard response={shown} />
          <TeamsCard response={shown} />
          <FilesTable response={shown} />
        </>
      ) : null}
    </div>
  );
}

/**
 * El resumen y el botón. En preview dice qué va a pasar; después de importar,
 * qué pasó. Es la misma tarjeta a propósito: los números tienen que coincidir.
 */
function SummaryCard({
  response,
  onImport,
  busy,
  allowed,
}: {
  response: DvwImportResponse;
  onImport: () => void;
  busy: boolean;
  allowed: boolean;
}): JSX.Element {
  const t = useT();
  const nuevos = importableFiles(response.files);
  const eventos = response.dryRun ? totalEvents(response.files) : response.summary.events;

  return (
    <Card title={response.dryRun ? t('import.previewCard') : t('import.resultCard')}>
      <ul className="hints">
        <li>
          {response.dryRun
            ? t('import.willImport', { matches: nuevos, events: eventos })
            : t('import.didImport', { matches: response.summary.imported, events: eventos })}
        </li>
        {response.summary.alreadyImported > 0 ? (
          <li>{t('import.alreadyImported', { count: response.summary.alreadyImported })}</li>
        ) : null}
        {response.summary.failed > 0 ? (
          <li>{t('import.failedFiles', { count: response.summary.failed })}</li>
        ) : null}
        {response.summary.unmapped > 0 ? (
          <li>{t('import.unmappedTotal', { count: response.summary.unmapped })}</li>
        ) : null}
        {!response.dryRun ? (
          <li>
            {t('import.teamsResult', {
              created: response.summary.teamsCreated,
              merged: response.summary.teamsMerged,
            })}
          </li>
        ) : null}
      </ul>

      {response.dryRun ? (
        <div className="row">
          <button
            type="button"
            className="primary"
            disabled={!allowed || busy || nuevos === 0}
            onClick={onImport}
            title={nuevos === 0 ? t('import.nothingToImport') : undefined}
          >
            {t('import.importButton', { count: nuevos })}
          </button>
          <span className="hint">{t('import.previewOnly')}</span>
        </div>
      ) : (
        <p className="hint">
          <a href="#/matches">{t('import.goToMatches')}</a>
        </p>
      )}
    </Card>
  );
}

/** El reporte honesto: qué NO se pudo traducir, con el código y una muestra. */
function UnmappedCard({ response }: { response: DvwImportResponse }): JSX.Element {
  const t = useT();
  const groups = rollupUnmapped(response.files);
  const warnings = [
    ...new Set(response.files.flatMap((file) => file.report?.warnings ?? [])),
  ];

  return (
    <Card title={t('import.unmappedCard')}>
      <p className="hint">{t('import.unmappedIntro')}</p>
      {warnings.length > 0 ? (
        <ul className="hints">
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}
      {groups.length === 0 ? (
        <Empty>{t('import.unmappedNone')}</Empty>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('import.colCode')}</th>
                <th>{t('import.colWhy')}</th>
                <th className="num">{t('import.colLines')}</th>
                <th className="num">{t('import.colFiles')}</th>
                <th>{t('import.colSample')}</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.code}>
                  <td>
                    <code>{group.code}</code>
                  </td>
                  <td>{t(`import.kind_${group.kind}` as MessageKey)}</td>
                  <td className="num">{group.count}</td>
                  <td className="num">{group.files}</td>
                  <td>
                    <code className="hint">{group.sample.slice(0, 60)}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** Los clubes del archivo, una vez cada uno (no una vez por fecha). */
function TeamsCard({ response }: { response: DvwImportResponse }): JSX.Element {
  const t = useT();
  const teams = rollupTeams(response.files);
  return (
    <Card title={t('import.teamsCard')}>
      {teams.length === 0 ? (
        <Empty>{t('import.teamsNone')}</Empty>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('import.colTeam')}</th>
                <th>{t('import.colState')}</th>
                <th className="num">{t('import.colNewPlayers')}</th>
                <th className="num">{t('import.colFiles')}</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => (
                <tr key={team.teamId}>
                  <td>
                    <strong>{team.name}</strong>
                    <div className="hint">{team.teamId}</div>
                  </td>
                  <td>{t(`import.teamState_${team.state}` as MessageKey)}</td>
                  <td className="num">{team.addedPlayers}</td>
                  <td className="num">{team.files}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** Archivo por archivo: qué partido es y qué le pasó. */
function FilesTable({ response }: { response: DvwImportResponse }): JSX.Element {
  const t = useT();
  return (
    <Card title={t('import.filesCard')}>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{t('import.colFile')}</th>
              <th>{t('import.colMatch')}</th>
              <th>{t('import.colDate')}</th>
              <th className="num">{t('import.colSets')}</th>
              <th className="num">{t('import.colEvents')}</th>
              <th className="num">{t('import.colUnmapped')}</th>
              <th>{t('import.colState')}</th>
            </tr>
          </thead>
          <tbody>
            {response.files.map((file, index) => (
              <FileRow key={file.report?.matchId ?? `${index}`} file={file} done={!response.dryRun} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function FileRow({ file, done }: { file: DvwFileResult; done: boolean }): JSX.Element {
  const t = useT();
  const report = file.report;
  return (
    <tr>
      <td>{report?.file ?? '·'}</td>
      <td>
        {report === null ? (
          '·'
        ) : done && file.outcome !== 'failed' ? (
          // Recién después de importar el partido existe y el link lleva a
          // algún lado; en el preview sería un 404 esperando.
          <a href={matchHash(report.matchId)}>{report.matchName}</a>
        ) : (
          report.matchName
        )}
        {report?.competition !== null && report?.competition !== undefined ? (
          <div className="hint">{report.competition}</div>
        ) : null}
      </td>
      <td>{report?.scheduledAt ? dateTime(report.scheduledAt) : '·'}</td>
      <td className="num">{report?.sets ?? 0}</td>
      <td className="num">{report?.events ?? 0}</td>
      <td className="num">{report?.unmappedCount ?? 0}</td>
      <td>
        {file.outcome === 'failed' ? (
          <span className="chip bad" title={file.error ?? undefined}>
            {t('import.outcome_failed')}
          </span>
        ) : (
          <span className={file.outcome === 'already_imported' ? 'chip muted' : 'chip ok'}>
            {t(`import.outcome_${file.outcome}` as MessageKey)}
          </span>
        )}
      </td>
    </tr>
  );
}
